import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { CheckIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useBullLabelsReport } from '@/services/reports';

import { useSemenPullContext } from './useSemenPullContext';

const extraLabelsSchema = z.object({
  extraLabels: z.coerce.number().int().min(0).optional(),
});

type ExtraLabelsFormValues = z.infer<typeof extraLabelsSchema>;

export function GenerateBullLabels({ date }: { date: string | null }) {
  const { t } = useTranslation();
  const [generateBullLabels] = useBullLabelsReport();

  const { extraSemenLabels, selectedSite } = useSemenPullContext();

  const [isOpen, setIsOpen] = useState(false);
  const { control, handleSubmit, reset } = useForm<ExtraLabelsFormValues>({
    resolver: zodResolver(extraLabelsSchema),
    defaultValues: { extraLabels: undefined },
  });

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger
          onClick={() => {
            reset();
          }}
          render={
            <Button
              variant="default"
              disabled={!date || !selectedSite}
              className="h-full"
            >
              {t('semen.Generate_Bull_Labels')}
            </Button>
          }
        />
        <PopoverContent align="start">
          <PopoverHeader>
            <PopoverTitle>{t('semen.Generate_Bull_Labels')}</PopoverTitle>
          </PopoverHeader>
          <FieldGroup className="gap-4">
            <Field orientation="horizontal">
              <FieldLabel htmlFor="date" className="w-1/2">
                {t('common.date')}
              </FieldLabel>
              <Input id="date" disabled value={date ?? ''} />
            </Field>
            <Field orientation="horizontal">
              <FieldLabel htmlFor="site" className="w-1/2">
                {t('site.site')}
              </FieldLabel>
              <Input id="site" disabled value={selectedSite?.name} />
            </Field>
            <Controller
              name="extraLabels"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <Field
                    orientation="horizontal"
                    data-invalid={fieldState.invalid}
                  >
                    <FieldLabel htmlFor="extraLabels" className="w-1/2">
                      {t('semen.extra_labels')}
                    </FieldLabel>
                    <Input
                      id="extraLabels"
                      type="number"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === '' ? undefined : e.target.value,
                        )
                      }
                      min={0}
                      step={1}
                      aria-invalid={fieldState.invalid}
                    />
                  </Field>
                  {fieldState.invalid && (
                    <FieldError
                      className="w-full"
                      errors={[fieldState.error]}
                    />
                  )}
                </>
              )}
            />
          </FieldGroup>
          <Button
            className="mt-2"
            onClick={handleSubmit(async (data) => {
              if (!date || !selectedSite) {
                return;
              }

              try {
                setIsOpen(false);
                await generateBullLabels({
                  donorScheduleDate: date,
                  fertilizationSiteId: selectedSite.id,
                  extraLabels: data.extraLabels,
                  extraSemenLabels: extraSemenLabels.filter(
                    (label) => label.extraLabels >= 1,
                  ),
                });
              } catch (error) {
                setIsOpen(true);
                console.error('Error generating bull labels:', error);
              }
            })}
          >
            <CheckIcon className="mr-2 size-4" /> {t('common.confirm')}
          </Button>
        </PopoverContent>
      </Popover>
    </>
  );
}
